import { MagazineHead } from "./_head";
import { Head, Link } from "@inertiajs/react";
import { Fragment, useState } from "react";
import type { ReactNode } from "react";
import {
  hairbookGlobalImageUrl,
  hairbookPostThumbnailUrl,
} from "@/lib/_url";
import { useVideoAutoplay } from "@/lib/_use-video-autoplay";
import { useVideoPlayer } from "@/lib/_use-video-player";
import { trackMagazineCta } from "./_measurement";
import { resolveMagazineHeroVideo } from "./_hero-video";
import type {
  MagazineArticle,
  MagazineArticleCard,
  MagazineBodyBlock,
  MagazineInlineContent,
  MagazineRelatedSalon,
  MagazineShowProps,
} from "./_props";

const accent = "#a4825a";
const accentDark = "#856843";
const softPink = "#b5557a";

export default function MagazineShow({
  article,
  relatedArticles = [],
  rankedArticles = [],
  nextArticle = null,
}: MagazineShowProps) {
  if (!article) {
    return (
      <main className="grid min-h-screen place-items-center bg-[#eef0f1]">
        <Head title="記事が見つかりません | Hairbook Magazine"><meta head-key="robots" name="robots" content="noindex" /></Head>
        <p className="text-sm text-black/40">Article not found.</p>
      </main>
    );
  }

  const inlineCtaBlockIndex = article.bodyBlocks.findIndex(
    (block) => block.type === "paragraph" && containsExternalUrl(blockContent(block)),
  );

  return (
    <>
      <MagazineHead article={article} />
      <div className="min-h-screen bg-[#eef0f1] text-[#2b2b2b] antialiased">
        <Header />

        <main className="mx-auto grid max-w-[1120px] gap-6 px-0 pb-20 lg:grid-cols-[1fr_320px] lg:px-8 lg:pt-6">
          <div className="overflow-hidden bg-white lg:rounded-2xl lg:border lg:border-[#e7e7e7]">
            <ArticleHero article={article} />
            <ArticleHeader article={article} />
            {article.relatedSalons.length > 0 && <a href="#salon-booking" className="mx-4 mb-5 block rounded-lg border border-[#a4825a] px-4 py-3 text-center text-sm text-[#856843] sm:mx-8">紹介サロンの料金・メニューを確認する</a>}

            <article className="px-4 pb-6 sm:px-8">
              <Toc blocks={article.bodyBlocks} />
              <ArticleBody
                blocks={article.bodyBlocks}
                article={article}
                inlineCtaBlockIndex={inlineCtaBlockIndex}
              />
            </article>

            <PrimarySalonCta salons={article.relatedSalons} fallbackArticle={article} />

            {relatedArticles.length > 0 && (
              <>
                <RecirculationSection
                  title={buildRelatedTitle(article)}
                  tone="gold"
                  articles={relatedArticles.slice(0, 4)}
                />
                <RecirculationSection
                  title="あなたにおすすめ"
                  tone="pink"
                  articles={relatedArticles.slice(4, 8).length > 0 ? relatedArticles.slice(4, 8) : relatedArticles.slice(0, 4)}
                  plain
                />
              </>
            )}

            {nextArticle && <UpNext article={nextArticle} />}
          </div>

          <RightRail rankedArticles={rankedArticles} relatedArticles={relatedArticles} />
        </main>
      </div>
    </>
  );
}

function Header() {
  return (
    <header className="sticky top-0 z-30 border-b border-[#e7e7e7] bg-white/90 backdrop-blur-md">
      <div className="mx-auto flex max-w-[1120px] items-center justify-center px-4 py-3 sm:px-6 lg:px-8">
        <Link href="/" className="text-center leading-none">
          <b className="font-serif text-xl font-semibold tracking-wide text-[#222]">Hairbook</b>
          <small className="mt-1 block text-[9px] tracking-[0.45em] text-[#8d8d8d]">MAGAZINE</small>
        </Link>
      </div>
    </header>
  );
}

function ArticleHero({ article }: { article: MagazineArticle }) {
  const hero = resolveMagazineHeroVideo(article);
  const [videoFailed, setVideoFailed] = useState(false);
  const fallbackImageUrl = articleImageUrl(article);
  const posterUrl = hero?.kind === "post" && article.embeds.hero_video_post_id
    ? hairbookPostThumbnailUrl(article.embeds.hero_video_post_id)
    : fallbackImageUrl;
  const playerSrc = !videoFailed && (hero?.kind === "r2" || hero?.kind === "post")
    ? hero.src
    : null;
  const globalVideoUrl = !videoFailed && hero?.kind === "global" ? hero.src : null;
  const { videoRef, isLoading } = useVideoPlayer({
    src: playerSrc,
    poster: posterUrl ?? undefined,
    muted: true,
    loop: true,
  });

  if (playerSrc) {
    return (
      <div className="relative aspect-[4/5] max-h-[72svh] w-full overflow-hidden bg-[#d8c3ad] lg:aspect-auto lg:h-[320px] lg:max-h-none">
        <video
          ref={videoRef}
          className="h-full w-full object-cover"
          muted
          loop
          playsInline
          poster={posterUrl ?? undefined}
          onError={() => setVideoFailed(true)}
        />
        {isLoading && (
          <div className="absolute inset-0">
            <ArticleImage article={article} className="h-full w-full object-cover" eager />
          </div>
        )}
      </div>
    );
  }

  if (globalVideoUrl) {
    return (
      <div className="relative aspect-[3/4] w-full overflow-hidden bg-[#0d0d12] lg:aspect-auto lg:h-[320px]">
        <video
          className="h-full w-full object-cover"
          muted
          loop
          autoPlay
          playsInline
          preload="metadata"
          onError={() => setVideoFailed(true)}
        >
          <source src={globalVideoUrl} />
        </video>
      </div>
    );
  }

  return (
    <div className="aspect-[4/5] max-h-[72svh] w-full bg-gradient-to-br from-[#f2c9d8] to-[#caa6b4] lg:aspect-auto lg:h-[320px] lg:max-h-none">
      <ArticleImage article={article} className="h-full w-full object-cover" eager />
    </div>
  );
}

function ArticleHeader({ article }: { article: MagazineArticle }) {
  const date = article.publishedAt
    ? new Date(article.publishedAt).toLocaleDateString("ja-JP", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      })
    : null;

  return (
    <header className="px-4 py-5 sm:px-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {article.tags[0] && (
          <Link
            href={`/?tag=${encodeURIComponent(article.tags[0])}`}
            className="rounded-full bg-[#f6f1e8] px-3 py-1 text-[10.5px] text-[#856843]"
          >
            {article.tags[0]}
          </Link>
        )}
        {date && <time className="text-[11px] text-[#8d8d8d]">{date}</time>}
      </div>
      <h1 className="mt-3 text-[21px] font-bold leading-[1.55] [text-wrap:pretty] sm:text-2xl lg:text-[28px]">
        {article.title}
      </h1>
      {article.tags.length > 1 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {article.tags.slice(1, 8).map((tag) => (
            <Link key={tag} href={`/?tag=${encodeURIComponent(tag)}`} className="rounded-full bg-[#f3f0ec] px-3 py-1 text-[10.5px] text-[#7a6a55]">
              {tag}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
}

function Toc({ blocks }: { blocks: MagazineBodyBlock[] }) {
  const headings = blocks
    .filter((block) => block.type === "heading")
    .map((block) => stripInlineUrls(inlineText(blockContent(block))))
    .filter(Boolean)
    .slice(0, 6);

  if (headings.length === 0) return null;

  return (
    <nav className="mb-6 rounded-xl bg-[#f6f1e8] px-4 py-3">
      <b className="text-[12px] tracking-wide text-[#856843]">目次</b>
      <ol className="mt-2 list-decimal space-y-1 pl-5">
        {headings.map((heading) => (
          <li key={heading} className="text-[12.5px] leading-6 text-[#5d5346]">
            {heading}
          </li>
        ))}
      </ol>
    </nav>
  );
}

function RecirculationSection({
  title,
  tone,
  articles,
  plain = false,
}: {
  title: string;
  tone: "gold" | "pink";
  articles: MagazineArticleCard[];
  plain?: boolean;
}) {
  return (
    <section className={`border-t border-[#e7e7e7] px-4 py-5 sm:px-6 ${plain ? "bg-white" : "bg-gradient-to-b from-[#fbf7f0] to-white"}`}>
      <SectionHeader title={title} tone={tone} />
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-4">
        {articles.map((article) => (
          <ArticleCard key={article.id} article={article} />
        ))}
      </div>
    </section>
  );
}

function PrimarySalonCta({
  salons,
  fallbackArticle,
}: {
  salons: MagazineRelatedSalon[];
  fallbackArticle: MagazineArticle;
}) {
  const salon = salons[0];
  if (!salon) return null;

  return (
    <>
      <section id="salon-booking" className="scroll-mt-24 mx-4 mt-4 overflow-hidden rounded-xl border-2 border-[#a4825a] bg-white sm:mx-6">
        <div className="flex items-center gap-2 bg-[#a4825a] px-4 py-3 text-[13px] font-bold text-white">
          <span className="rounded bg-white px-2 py-0.5 font-serif text-[12px] tracking-wide text-[#856843]">Hairbook</span>
          掲載サロンの詳細・予約へ
        </div>
        <div className="flex gap-3 p-4">
          <div className="size-20 shrink-0 overflow-hidden rounded-lg bg-[#e7ddcd] sm:size-24">
            <ArticleImage article={fallbackArticle} className="h-full w-full object-cover" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-[15px] font-bold">{salon.name}</h2>
            <p className="mt-1 text-[11.5px] text-[#8d8d8d]">
              <span className="text-[#856843]">詳細・予約先：Hairbook / Hot Pepper Beauty</span>
            </p>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {fallbackArticle.tags.slice(0, 4).map((tag) => (
                <span key={tag} className="rounded-full bg-[#f6f1e8] px-3 py-1 text-[10.5px] text-[#856843]">
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
        <a
          href={`/redirect?hpb_salon_id=${encodeURIComponent(salon.hpbSalonId)}&article_id=${encodeURIComponent(fallbackArticle.id)}`}
          onClick={() => trackMagazineCta(fallbackArticle.id, salon.hpbSalonId)}
          className="mx-4 mb-2 flex items-center justify-center rounded-lg bg-[#a4825a] px-4 py-3 text-[14px] font-bold text-white"
        >
          料金・メニューを確認する
        </a>
        <a
          href={`/redirect?hpb_salon_id=${encodeURIComponent(salon.hpbSalonId)}&article_id=${encodeURIComponent(fallbackArticle.id)}`}
          onClick={() => trackMagazineCta(fallbackArticle.id, salon.hpbSalonId)}
          className="mb-4 block text-center text-[12px] text-[#8d8d8d]"
        >
          サロンの詳細を見る
        </a>
      </section>

      <div className="fixed inset-x-0 bottom-0 z-20 flex items-center gap-3 border-t border-[#e7e7e7] bg-white/95 px-4 py-3 pb-[calc(env(safe-area-inset-bottom)+12px)] backdrop-blur sm:px-6 lg:hidden">
        <div className="min-w-0 flex-1 leading-tight">
          <b className="block truncate text-[12.5px]">{salon.name}</b>
          <small className="block truncate text-[10.5px] text-[#8d8d8d]">Hairbook掲載サロン</small>
        </div>
        <a
          href={`/redirect?hpb_salon_id=${encodeURIComponent(salon.hpbSalonId)}&article_id=${encodeURIComponent(fallbackArticle.id)}`}
          onClick={() => trackMagazineCta(fallbackArticle.id, salon.hpbSalonId)}
          className="shrink-0 rounded-lg bg-[#a4825a] px-5 py-3 text-[13px] font-bold text-white"
        >
          料金・予約
        </a>
      </div>
    </>
  );
}

function UpNext({ article }: { article: MagazineArticleCard }) {
  return (
    <Link
      href={`/articles/${article.id}/`}
      className="mx-4 my-5 flex flex-col items-center justify-center gap-1 rounded-lg border-2 border-[#a4825a] bg-white px-4 py-3 text-center text-[14px] font-bold text-[#856843] sm:mx-6 sm:flex-row sm:gap-2"
    >
      次の記事を続けて読む
      <small className="font-normal opacity-70 sm:before:content-['｜_']">{article.tags[0] ?? "Magazine"}</small>
    </Link>
  );
}

function RightRail({
  rankedArticles,
  relatedArticles,
}: {
  rankedArticles: MagazineArticleCard[];
  relatedArticles: MagazineArticleCard[];
}) {
  return (
    <aside className="sticky top-[84px] hidden self-start lg:block">
      <RailCard title="最近公開された記事" tone="gold" articles={rankedArticles.slice(0, 5)} />
      <RailCard title="関連記事" tone="pink" articles={relatedArticles.slice(0, 5)} ranked={false} />
    </aside>
  );
}

function RailCard({
  title,
  tone,
  articles,
  ranked,
}: {
  title: string;
  tone: "gold" | "pink";
  articles: MagazineArticleCard[];
  ranked?: boolean;
}) {
  if (articles.length === 0) return null;

  return (
    <section className="mb-4 rounded-[14px] border border-[#e7e7e7] bg-white p-4">
      <SectionHeader title={title} tone={tone} compact />
      <div className="mt-2">
        {articles.map((article, index) => (
          <MiniArticle key={article.id} article={article} rank={ranked ? index + 1 : undefined} />
        ))}
      </div>
    </section>
  );
}

function MiniArticle({ article, rank }: { article: MagazineArticleCard; rank?: number }) {
  return (
    <Link href={`/articles/${article.id}/`} className="flex gap-3 border-t border-[#f0f0f0] py-2 first:border-t-0">
      {rank && <div className="w-5 shrink-0 font-serif text-lg font-bold text-[#a4825a]">{rank}</div>}
      <div className="size-[52px] shrink-0 overflow-hidden rounded-lg bg-[#e7ddcd]">
        <ArticleImage article={article} className="h-full w-full object-cover" />
      </div>
      <h3 className="line-clamp-2 text-[11.5px] font-semibold leading-[1.45]">{article.title}</h3>
    </Link>
  );
}

function SectionHeader({
  title,
  tone,
  compact = false,
}: {
  title: string;
  tone: "gold" | "pink";
  compact?: boolean;
}) {
  return (
    <div className="flex items-end justify-between gap-4">
      <h2 className={`flex items-center gap-2 font-bold ${compact ? "text-[13px]" : "text-[17px]"}`}>
        <span style={{ color: tone === "gold" ? accent : softPink }}>◆</span>
        {title}
      </h2>
    </div>
  );
}

function ArticleCard({ article }: { article: MagazineArticleCard }) {
  return (
    <Link href={`/articles/${article.id}/`} className="group grid grid-cols-[112px_1fr] overflow-hidden rounded-lg border border-[#e7e7e7] bg-white transition hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(0,0,0,0.10)] sm:block">
      <div className="aspect-square bg-[#e7ddcd] sm:aspect-[4/3]">
        <ArticleImage article={article} className="h-full w-full object-cover transition duration-700 group-hover:scale-105" />
      </div>
      <div className="px-3 py-3">
        <h3 className="line-clamp-3 text-[13px] font-semibold leading-[1.5] sm:text-[12.5px] sm:leading-[1.45]">{article.title}</h3>
        <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[10px] text-[#8d8d8d]">
          {article.tags[0] && <span style={{ color: accentDark }}>◍ {article.tags[0]}</span>}
          {article.publishedAt && <span>{formatShortDate(article.publishedAt)}</span>}
        </div>
      </div>
    </Link>
  );
}

function ArticleImage({
  article,
  className,
  eager = false,
}: {
  article: MagazineArticleCard;
  className: string;
  eager?: boolean;
}) {
  const videoRef = useVideoAutoplay<HTMLVideoElement>();
  const videoUrl = article.globalImageId && article.globalImageCategory === "video"
    ? hairbookGlobalImageUrl(article.globalImageId)
    : null;

  if (videoUrl) {
    return (
      <video
        ref={videoRef}
        src={videoUrl}
        className={className}
        muted
        loop
        playsInline
        preload={eager ? "auto" : "metadata"}
      />
    );
  }

  const imageUrl = articleImageUrl(article);

  if (imageUrl) {
    return (
      <img
        src={imageUrl}
        alt=""
        className={className}
        loading={eager ? "eager" : "lazy"}
        referrerPolicy="no-referrer"
      />
    );
  }

  return (
    <div className={`${className} grid place-items-center bg-gradient-to-br from-[#e9ddcc] to-[#d8c3ad] px-3 text-center text-[11px] font-semibold text-white`}>
      {article.title}
    </div>
  );
}

function articleImageUrl(article: MagazineArticleCard): string | null {
  const globalImageUrl = article.globalImageId && article.globalImageCategory !== "video"
    ? hairbookGlobalImageUrl(article.globalImageId)
    : null;

  return globalImageUrl || article.eyecatchUrl;
}

function ArticleBody({
  blocks,
  article,
  inlineCtaBlockIndex,
}: {
  blocks: MagazineBodyBlock[];
  article: MagazineArticle;
  inlineCtaBlockIndex: number;
}) {
  const renderedBlocks: ReactNode[] = [];

  for (let index = 0; index < blocks.length; index += 1) {
    const block = blocks[index];

    if (
      block.type === "bulletListItem"
      || block.type === "numberedListItem"
      || block.type === "checkListItem"
    ) {
      const listType = block.type;
      const items: MagazineBodyBlock[] = [];

      while (blocks[index]?.type === listType) {
        items.push(blocks[index]);
        index += 1;
      }

      index -= 1;
      renderedBlocks.push(<ListBlock key={index} type={listType} items={items} />);
      continue;
    }

    renderedBlocks.push(
      <BlockRenderer
        key={index}
        block={block}
        article={article}
        showInlineCta={index === inlineCtaBlockIndex}
      />,
    );
  }

  return renderedBlocks;
}

function BlockRenderer({ block, article, showInlineCta }: { block: MagazineBodyBlock; article: MagazineArticle; showInlineCta: boolean }) {
  const { type } = block;

  if (type === "heading") return <HeaderBlock block={block} />;
  if (type === "paragraph") return <ParagraphBlock block={block} article={article} showInlineCta={showInlineCta} />;
  if (type === "image") return <ImageBlock block={block} />;
  if (type === "divider" || type === "delimiter") return <DividerBlock />;
  if (type === "quote") return <QuoteBlock block={block} />;

  return null;
}

function HeaderBlock({ block }: { block: MagazineBodyBlock }) {
  const level = (block.props.level as number) ?? 2;
  const content = blockContent(block);
  if (!stripInlineUrls(inlineText(content)).trim()) return null;

  const Tag = level <= 2 ? "h2" : "h3";
  const sizeClass =
    level <= 2
      ? "mt-8 mb-3 border-l-4 border-[#a4825a] pl-3 text-[16.5px] font-bold leading-7"
      : "mt-7 mb-3 text-[15px] font-bold leading-7";

  return <Tag className={sizeClass}>{renderInline(content)}</Tag>;
}

function ParagraphBlock({ block, article, showInlineCta }: { block: MagazineBodyBlock; article: MagazineArticle; showInlineCta: boolean }) {
  const content = blockContent(block);
  const text = inlineText(content);
  if (!stripInlineUrls(text).trim()) return null;
  const salon = showInlineCta ? article.relatedSalons[0] : null;

  return (
    <>
      <p className="mb-5 text-[14px] leading-8 text-[#3a3a3a] sm:text-[15px] sm:leading-9">
        {renderInline(content)}
      </p>
      {salon && <InlineSalonCta salon={salon} fallbackArticle={article} />}
    </>
  );
}

function InlineSalonCta({
  salon,
  fallbackArticle,
}: {
  salon: MagazineRelatedSalon;
  fallbackArticle: MagazineArticle;
}) {
  return (
    <section className="my-7 overflow-hidden rounded-2xl border border-[#d8c3ad] bg-[#fffaf3] shadow-[0_12px_32px_rgba(70,45,22,0.08)]">
      <div className="flex gap-3 p-4">
        <div className="size-24 shrink-0 overflow-hidden rounded-xl bg-[#e7ddcd]">
          <ArticleImage article={fallbackArticle} className="h-full w-full object-cover" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] font-bold tracking-wide text-[#a4825a]">Hairbook掲載サロン</p>
          <h2 className="mt-1 line-clamp-2 text-[16px] font-bold leading-6 text-[#2b2b2b]">{salon.name}</h2>
          <p className="mt-2 text-[12px] leading-5 text-[#756b5f]">記事で紹介しているサロンの料金・メニューを確認できます。遷移先はHairbookまたはHot Pepper Beautyです。</p>
        </div>
      </div>
      <a
        href={`/redirect?hpb_salon_id=${encodeURIComponent(salon.hpbSalonId)}&article_id=${encodeURIComponent(fallbackArticle.id)}`}
          onClick={() => trackMagazineCta(fallbackArticle.id, salon.hpbSalonId)}
        className="mx-4 mb-4 flex items-center justify-center rounded-xl bg-[#a4825a] px-4 py-3 text-[14px] font-bold text-white"
      >
        このサロンを予約する
      </a>
    </section>
  );
}

function ImageBlock({ block }: { block: MagazineBodyBlock }) {
  const caption = block.props.caption as string;
  const url = block.props.url as string;
  if (!url) return null;

  return (
    <figure className="my-7">
      <img src={url} alt="" className="w-full rounded-xl" loading="lazy" referrerPolicy="no-referrer" />
      {caption && (
        <figcaption className="mt-2 px-2 text-center text-[11px] text-black/40">
          {caption}
        </figcaption>
      )}
    </figure>
  );
}

function ListBlock({ type, items }: { type: string; items: MagazineBodyBlock[] }) {
  if (items.length === 0) return null;

  const Tag = type === "numberedListItem" ? "ol" : "ul";
  const style = type === "numberedListItem"
    ? "ordered"
    : type === "checkListItem"
      ? "check"
      : "unordered";

  return (
    <Tag className={`my-5 space-y-2 pl-6 text-sm leading-7 text-[#3a3a3a] ${style === "ordered" ? "list-decimal" : style === "check" ? "list-none" : "list-disc"}`}>
      {items.map((item, index) => (
        <li key={index}>
          {type === "checkListItem" ? `${item.props.checked ? "☑" : "☐"} ` : null}
          {renderInline(blockContent(item))}
        </li>
      ))}
    </Tag>
  );
}

function DividerBlock() {
  return (
    <div className="my-10 flex items-center justify-center">
      <div className="h-px w-12 bg-black/15" />
    </div>
  );
}

function QuoteBlock({ block }: { block: MagazineBodyBlock }) {
  const caption = block.props.caption as string;
  const content = blockContent(block);
  if (!stripInlineUrls(inlineText(content)).trim()) return null;

  return (
    <blockquote className="my-8 border-l-4 border-[#a4825a]/40 pl-5">
      <p className="text-sm leading-7 text-black/60 italic">{renderInline(content)}</p>
      {caption && <cite className="mt-2 block text-[11px] text-black/40 not-italic">{caption}</cite>}
    </blockquote>
  );
}

function buildRelatedTitle(article: MagazineArticle) {
  return "関連するテーマの記事";
}

function formatShortDate(value: string) {
  return new Date(value).toLocaleDateString("ja-JP", {
    month: "numeric",
    day: "numeric",
  });
}

function blockContent(block: MagazineBodyBlock): MagazineInlineContent[] {
  return block.content ?? [];
}

function renderInline(content: MagazineInlineContent[]): ReactNode[] {
  return content.flatMap((inlineContent, index) => {
    if (inlineContent.type === "link") {
      return <Fragment key={index}>{renderInline(inlineContent.content)}</Fragment>;
    }

    const text = stripInlineUrls(inlineContent.text);
    if (!text) return [];

    let node: ReactNode = text.split("\n").map((line, lineIndex) => (
      <Fragment key={lineIndex}>
        {lineIndex > 0 && <br />}
        {line}
      </Fragment>
    ));

    if (inlineContent.styles.backgroundColor) node = <mark>{node}</mark>;
    if (inlineContent.styles.underline) node = <u>{node}</u>;
    if (inlineContent.styles.italic) node = <i>{node}</i>;
    if (inlineContent.styles.bold) node = <b>{node}</b>;

    return <Fragment key={index}>{node}</Fragment>;
  });
}

function inlineText(content: MagazineInlineContent[]): string {
  return content
    .map((inlineContent) =>
      inlineContent.type === "link"
        ? inlineText(inlineContent.content)
        : inlineContent.text,
    )
    .join("");
}

function containsExternalUrl(content: MagazineInlineContent[]): boolean {
  return /https?:\/\/[^\s<>"']+/i.test(
    content
      .map((inlineContent) =>
        inlineContent.type === "link"
          ? `${inlineContent.href} ${inlineText(inlineContent.content)}`
          : inlineContent.text,
      )
      .join(" "),
  );
}

function stripInlineUrls(text: string): string {
  return text.replace(/https?:\/\/[^\s<>"']+/gi, "").replace(/\s{2,}/g, " ").trim();
}
